import React, { useEffect, useState } from "react";
import {
    Card, Row, Col, Divider, Statistic, Table, Progress, Tag, Tooltip,
    Collapse, Spin, Alert, Badge, Typography, Space, Segmented,
} from "antd";
import {
    UsergroupAddOutlined, EnvironmentOutlined, BarChartOutlined,
    UserOutlined, TeamOutlined, FileTextOutlined,
    ExperimentOutlined, HeartOutlined, RiseOutlined, FallOutlined,
    HomeOutlined, CheckCircleOutlined, BookOutlined, ManOutlined,
    WomanOutlined, ApartmentOutlined,
} from "@ant-design/icons";
import GraficaTorta from "./Graficos/GraficaTorta";
import GraficaBarras from "./Graficos/GraficaBarras";
import GraficaBarrasHorizontal from "./Graficos/GraficaBarrasHorizontal";
import GraficaTorta3D from "./Graficos/GraficaTorta3D";
import { selectAppPermissions } from "../../store/auth/selectors";
import { useSelector } from "react-redux";
import { dataIndicadores } from "../../services/plaOperativo/planOperativoProyectosServices";

const { Title, Text } = Typography;

// ─── Paleta centralizada ─────────────────────────────────────────────────────
const COLORS = {
    primary:   "#1D6FA4",
    secondary: "#2E9E6B",
    accent:    "#F4831F",
    warning:   "#F5A623",
    danger:    "#E84040",
    purple:    "#7B5EA7",
    teal:      "#2BBCB0",
    slate:     "#5C7A99",
    bgSection: "#F0F5FB",
    bgCard:    "#FFFFFF",
};

const CHART_COLORS = {
    main:      [COLORS.primary, COLORS.secondary, COLORS.accent, COLORS.purple, COLORS.teal, COLORS.slate],
    sexo:      [COLORS.primary, COLORS.accent],
    ippta:     ["#E07B2C", "#B8A9CC", "#4FA8D4"],
    medGen:    [COLORS.secondary, COLORS.accent],
    edha:      [COLORS.primary, "#B8A9CC", COLORS.accent],
    visitas:   ["#4FA8D4"],
    comercial: [COLORS.primary, COLORS.accent, COLORS.warning],
};

// Mapa de colores para niveles de escolaridad
const ESCOLARIDAD_COLORS = [
    COLORS.primary, COLORS.secondary, COLORS.accent, COLORS.purple,
    COLORS.teal, COLORS.slate, COLORS.warning, COLORS.danger,
];

// ─── KPICard ─────────────────────────────────────────────────────────────────
const KPICard = ({ titulo, valor, icono: Icono, color = COLORS.primary, suffix = "" }) => (
    <Card
        hoverable
        style={{
            borderRadius: 12,
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            borderTop: `3px solid ${color}`,
            height: "100%",
        }}
        styles={{ body: { padding: "20px 16px" } }}
    >
        <Space direction="vertical" align="center" style={{ width: "100%" }}>
            <div style={{
                width: 52, height: 52, borderRadius: "50%",
                background: `${color}18`,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <Icono style={{ fontSize: 24, color }} />
            </div>
            <Statistic
                value={valor}
                suffix={suffix}
                valueStyle={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1 }}
            />
            <Text style={{ fontSize: 12, color: "#666", textAlign: "center", lineHeight: 1.3 }}>
                {titulo}
            </Text>
        </Space>
    </Card>
);

// ─── SeccionTitulo ────────────────────────────────────────────────────────────
const SeccionTitulo = ({ icono: Icono, titulo, color = COLORS.primary }) => (
    <div style={{
        display: "flex", alignItems: "center", gap: 10,
        margin: "28px 0 16px",
        paddingBottom: 10,
        borderBottom: `2px solid ${color}22`,
    }}>
        <div style={{
            background: color, borderRadius: 8,
            width: 34, height: 34,
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <Icono style={{ fontSize: 18, color: "#fff" }} />
        </div>
        <Title level={4} style={{ margin: 0, color: "#02172F", fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>
            {titulo}
        </Title>
    </div>
);

// ─── GraficaCard ─────────────────────────────────────────────────────────────
// minHeight controla la altura del body para que la gráfica tenga espacio real
const GraficaCard = ({ titulo, children, minHeight = 280 }) => (
    <Card
        title={<Text strong style={{ fontSize: 13, color: "#fff" }}>{titulo}</Text>}
        style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.07)", height: "100%" }}
        styles={{
            header: { background: "#02172F", borderRadius: "12px 12px 0 0", padding: "10px 16px", minHeight: 42 },
            body: { padding: 16, minHeight, display: "flex", alignItems: "stretch" },
        }}
    >
        {/* El wrapper ocupa toda la altura disponible */}
        <div style={{ width: "100%", height: minHeight }}>
            {children}
        </div>
    </Card>
);

// ─── EscolaridadCard — Card rica por nivel ───────────────────────────────────
const EscolaridadCard = ({ item, total, index }) => {
    const pct = total > 0 ? +((item.total_por_nivel / total) * 100).toFixed(1) : 0;
    const color = ESCOLARIDAD_COLORS[index % ESCOLARIDAD_COLORS.length];

    return (
        <Tooltip
            title={
                <Space direction="vertical" size={2}>
                    <Text style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>{item.nivel_escolaridad}</Text>
                    <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 11 }}>
                        {item.total_por_nivel} participantes · {pct}% del total
                    </Text>
                </Space>
            }
            placement="top"
        >
            <Card
                hoverable
                style={{
                    borderRadius: 10,
                    borderLeft: `4px solid ${color}`,
                    cursor: "default",
                    transition: "box-shadow 0.2s",
                }}
                styles={{ body: { padding: "12px 14px" } }}
            >
                {/* Encabezado: ícono + nombre + badge */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: `${color}18`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                    }}>
                        <BookOutlined style={{ color, fontSize: 14 }} />
                    </div>
                    <Text
                        style={{ fontSize: 12, fontWeight: 600, flex: 1, lineHeight: 1.3, color: "#2c3e50" }}
                        ellipsis={{ tooltip: item.nivel_escolaridad }}
                    >
                        {item.nivel_escolaridad}
                    </Text>
                    <Tag
                        color="blue"
                        style={{ marginLeft: "auto", fontWeight: 700, fontSize: 12, flexShrink: 0 }}
                    >
                        {item.total_por_nivel}
                    </Tag>
                </div>

                {/* Barra de progreso */}
                <Progress
                    percent={pct}
                    size="small"
                    strokeColor={color}
                    trailColor="#f0f0f0"
                    format={(p) => (
                        <Text style={{ fontSize: 11, color, fontWeight: 700 }}>{p}%</Text>
                    )}
                />

                {/* Pie informativo */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    <Text style={{ fontSize: 10, color: "#999" }}>
                        {pct >= 50 ? "Nivel predominante" : pct >= 20 ? "Nivel frecuente" : "Nivel menor"}
                    </Text>
                    <Text style={{ fontSize: 10, color }}>
                        {pct}% del total
                    </Text>
                </div>
            </Card>
        </Tooltip>
    );
};

// ─── EstadoCivilCard ──────────────────────────────────────────────────────────
const EstadoCivilCard = ({ item, total, index }) => {
    const pct = total > 0 ? +((item.total_por_estado / total) * 100).toFixed(1) : 0;
    const color = [COLORS.accent, COLORS.purple, COLORS.teal, COLORS.secondary, COLORS.slate][index % 5];

    return (
        <Tooltip title={`${item.estado_civil}: ${item.total_por_estado} personas (${pct}%)`} placement="top">
            <Card
                hoverable
                style={{ borderRadius: 10, borderLeft: `4px solid ${color}` }}
                styles={{ body: { padding: "12px 14px" } }}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, fontWeight: 600, color: "#2c3e50" }}>{item.estado_civil}</Text>
                    <Tag color="orange" style={{ fontWeight: 700 }}>{item.total_por_estado}</Tag>
                </div>
                <Progress
                    percent={pct}
                    size="small"
                    strokeColor={color}
                    format={(p) => <Text style={{ fontSize: 11, color, fontWeight: 700 }}>{p}%</Text>}
                />
            </Card>
        </Tooltip>
    );
};

// ─── Columnas tabla línea productiva ─────────────────────────────────────────
const columnsLineap = [
    {
        title: "Línea Productiva",
        dataIndex: "linea_economica",
        key: "lineaEconomica",
        render: (text) => <Text strong style={{ color: COLORS.primary }}>{text}</Text>,
    },
    {
        title: "Participantes",
        dataIndex: "total_por_linea_economica",
        key: "total",
        align: "center",
        render: (total) => (
            <Tag color={total > 5 ? "green" : "orange"} style={{ fontWeight: 700, fontSize: 13 }}>
                {total}
            </Tag>
        ),
    },
];

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const DashboardPlanOperativo = ({ dataProyecto, codigoProyecto }) => {
    const [dataParticipantes, setDataParticipantes] = useState(false);
    const [datosDashboard, setDatosDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const participantesComponent = dataProyecto?.find((item) => item.sigla === "LP");
    const resumenProyectos = participantesComponent?.resumenProyectos || {};
    const detalleParticipantes = participantesComponent?.detalleParticipantes || [];

    const appName = "dashboardPlanOperativo";
    const permissions = useSelector((state) => selectAppPermissions(state, appName));
    // permissionPrint reservado para futura funcionalidad de impresión
    const permissionPrint = permissions.priv_print === "Y";

    useEffect(() => {
        const fetchData = async () => {
            if (!dataProyecto || !codigoProyecto) {
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                setError(null);
                const response = await dataIndicadores(codigoProyecto);
                if (response.status === 200) {
                    setDatosDashboard(response.data);
                } else {
                    setError("No se pudieron obtener los datos del dashboard.");
                }
            } catch (err) {
                console.error("Error al obtener los indicadores:", err);
                setError("Ocurrió un error al cargar los datos.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [codigoProyecto]);

    useEffect(() => {
        setDataParticipantes(
            !!(
                participantesComponent &&
                resumenProyectos.totalParticipante > 0 &&
                Array.isArray(detalleParticipantes) &&
                detalleParticipantes.length > 0
            )
        );
    }, [participantesComponent, resumenProyectos, detalleParticipantes]);

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                <Spin size="large" tip="Cargando dashboard del proyecto..." />
            </div>
        );
    }

    if (error) {
        return <Alert type="error" message={error} showIcon style={{ margin: 24 }} />;
    }

    if (!datosDashboard) return null;

    const {
        indicadores,
        caracterizacionParticipantes,
        visitasAcompanamiento,
        ecas,
        actasEntrega,
        dataIPPTA,
        dataEDHA,
        componenteComercial,
    } = datosDashboard;

    const {
        totalParticipante = 0,
        participantesPorDepartamento = [],
        participantesPorSexo = [],
        conteoPorCategorias = [],
        participantesPorNivelEscolaridad = [],
        participantesPorEstadoCivil = [],
        organizaciones = [],
        cabezaFamiliar = [],
        municipio = [],
        lineaEconomica = [],
        zona = [],
    } = resumenProyectos;

    const department = participantesPorDepartamento?.[0];
    const muni = municipio?.length > 0 ? municipio[0] : null;
    const totalCabeza = cabezaFamiliar.reduce((acc, curr) => acc + curr.cantidad, 0);

    // Filtrar claves "totalArchivos" de objetos de visitas/ecas/actas
    const visitasKeys = Object.keys(visitasAcompanamiento?.numeroVisitas ?? {}).filter((k) => k !== "totalArchivos");
    const visitasVals = visitasKeys.map((k) => visitasAcompanamiento.numeroVisitas[k]);

    const ecasKeys = Object.keys(ecas?.numeroEcas ?? {}).filter((k) => k !== "totalArchivos");
    const ecasVals = ecasKeys.map((k) => ecas.numeroEcas[k]);

    const actasKeys = Object.keys(actasEntrega?.numeroActasEntrega ?? {}).filter((k) => k !== "totalArchivos");
    const actasVals = actasKeys.map((k) => actasEntrega.numeroActasEntrega[k]);

    // KPIs principales
    const kpis = [
        { titulo: "Total Participantes",  valor: indicadores?.numeroParticipantes ?? totalParticipante ?? 0, icono: UsergroupAddOutlined, color: COLORS.primary },
        { titulo: "Número de Visitas",    valor: indicadores?.numeroVisitas ?? 0,       icono: EnvironmentOutlined, color: COLORS.secondary },
        { titulo: "ECA's Realizadas",     valor: indicadores?.numeroEcas ?? 0,          icono: BarChartOutlined,    color: COLORS.accent },
        { titulo: "Actas de Entrega",     valor: indicadores?.numeroActasEntrega ?? 0,  icono: FileTextOutlined,    color: COLORS.purple },
        { titulo: "Encuestas IPPTA",      valor: indicadores?.ippta ?? 0,               icono: ExperimentOutlined,  color: COLORS.teal },
        { titulo: "Encuestas EDHA",       valor: indicadores?.edah ?? 0,                icono: HeartOutlined,       color: COLORS.slate },
    ];

    return (
        <div style={{ padding: "16px 20px", background: COLORS.bgSection, minHeight: "100vh" }}>

            {/* ── Encabezado ───────────────────────────────────────────── */}
            <div style={{
                background: `#02172F`,
                borderRadius: 14,
                padding: "20px 24px",
                marginBottom: 24,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 12,
            }}>
                <div>
                    <Title level={3} style={{ color: "#fff", margin: 0, fontSize: 20 }}>
                        Dashboard — Plan Operativo Anual
                    </Title>
                    <Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 13 }}>
                        Proyecto #{codigoProyecto} · Seguimiento de indicadores y caracterización
                    </Text>
                </div>
                <Space wrap>
                    {department && (
                        <Tag icon={<BarChartOutlined />} color="blue" style={{ fontSize: 13, padding: "4px 10px" }}>
                            {department.departamento}
                        </Tag>
                    )}
                    {muni && (
                        <Tag icon={<EnvironmentOutlined />} color="green" style={{ fontSize: 13, padding: "4px 10px" }}>
                            {muni.municipio}
                        </Tag>
                    )}
                </Space>
            </div>

            {/* ── KPIs ─────────────────────────────────────────────────── */}
            <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
                {kpis.map((kpi, i) => (
                    <Col key={i} xs={12} sm={8} md={8} lg={4}>
                        <KPICard {...kpi} />
                    </Col>
                ))}
            </Row>

            {/* ── Acompañamiento Técnico ───────────────────────────────── */}
            <SeccionTitulo icono={BarChartOutlined} titulo="ACOMPAÑAMIENTO TÉCNICO" color={COLORS.secondary} />
            <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="Visitas de acompañamiento por periodo" minHeight={260}>
                        <GraficaBarrasHorizontal
                            etiquetas={visitasKeys}
                            datos={[{ label: "Visitas", valores: visitasVals }]}
                            colores={CHART_COLORS.visitas}
                        />
                    </GraficaCard>
                </Col>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="ECA's por periodo" minHeight={260}>
                        <GraficaBarrasHorizontal
                            etiquetas={ecasKeys}
                            datos={[{ label: "ECA's", valores: ecasVals }]}
                            colores={[COLORS.secondary]}
                        />
                    </GraficaCard>
                </Col>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="Actas de entrega por periodo" minHeight={260}>
                        <GraficaBarrasHorizontal
                            etiquetas={actasKeys}
                            datos={[{ label: "Actas", valores: actasVals }]}
                            colores={[COLORS.purple]}
                        />
                    </GraficaCard>
                </Col>
            </Row>

            {/* ── IPPTA ────────────────────────────────────────────────── */}
            <SeccionTitulo icono={ExperimentOutlined} titulo="IPPTA — Índice de Práctica Productiva y Transición Agroecológica" color={COLORS.teal} />

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                    <Card style={{ borderRadius: 12, textAlign: "center", borderTop: `3px solid ${COLORS.accent}` }}
                          styles={{ body: { padding: 16 } }}>
                        <Statistic
                            title={<Text style={{ fontSize: 12 }}>Registros Medición Inicial</Text>}
                            value={dataIPPTA?.ippta?.medicionGeneral?.medicionInicial ?? 0}
                            valueStyle={{ color: COLORS.accent, fontWeight: 700 }}
                            prefix={<RiseOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card style={{ borderRadius: 12, textAlign: "center", borderTop: `3px solid ${COLORS.teal}` }}
                          styles={{ body: { padding: 16 } }}>
                        <Statistic
                            title={<Text style={{ fontSize: 12 }}>Registros Medición Final</Text>}
                            value={dataIPPTA?.ippta?.medicionGeneral?.medicionFinal ?? 0}
                            valueStyle={{ color: COLORS.teal, fontWeight: 700 }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                {[
                    {
                        dim: "Ambiental",
                        ini: dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionInicial?.dimensionAmbiental ?? 0,
                        fin: dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionFinal?.dimensionAmbiental ?? 0,
                    },
                    {
                        dim: "Socioeconómica",
                        ini: dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionInicial?.dimensionSocioeconomica ?? 0,
                        fin: dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionFinal?.dimensionSocioeconomica ?? 0,
                    },
                    {
                        dim: "Productiva",
                        ini: dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionInicial?.dimensionProductiva ?? 0,
                        fin: dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionFinal?.dimensionProductiva ?? 0,
                    },
                ].map(({ dim, ini: iniRaw, fin: finRaw }) => {
                    const ini = Number(iniRaw) || 0;
                    const fin = Number(finRaw) || 0;
                    const diff = fin - ini;
                    return (
                        <Col key={dim} xs={24} sm={4}>
                            <Tooltip title={`Inicial: ${ini.toFixed(1)} → Final: ${fin.toFixed(1)}`}>
                                <Card style={{ borderRadius: 12, textAlign: "center" }} styles={{ body: { padding: 12 } }} hoverable>
                                    <Text style={{ fontSize: 11, color: "#888", display: "block" }}>{dim}</Text>
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 4 }}>
                                        {diff >= 0
                                            ? <RiseOutlined style={{ color: COLORS.secondary }} />
                                            : <FallOutlined style={{ color: COLORS.danger }} />}
                                        <Text strong style={{ fontSize: 16, color: diff >= 0 ? COLORS.secondary : COLORS.danger }}>
                                            {diff >= 0 ? "+" : ""}{diff.toFixed(1)}
                                        </Text>
                                    </div>
                                    <Text style={{ fontSize: 10, color: "#aaa" }}>variación promedio</Text>
                                </Card>
                            </Tooltip>
                        </Col>
                    );
                })}
            </Row>

            <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="Dimensiones — Medición Inicial (promedio)" minHeight={280}>
                        <GraficaBarras
                            etiquetas={["Ambiental", "Socioeconómica", "Productiva"]}
                            datos={[{
                                label: "Medición Inicial",
                                valores: [
                                    +Number(dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionInicial?.dimensionAmbiental ?? 0).toFixed(1),
                                    +Number(dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionInicial?.dimensionSocioeconomica ?? 0).toFixed(1),
                                    +Number(dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionInicial?.dimensionProductiva ?? 0).toFixed(1),
                                ],
                            }]}
                            colores={CHART_COLORS.ippta}
                        />
                    </GraficaCard>
                </Col>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="Dimensiones — Medición Final (promedio)" minHeight={280}>
                        <GraficaBarras
                            etiquetas={["Ambiental", "Socioeconómica", "Productiva"]}
                            datos={[{
                                label: "Medición Final",
                                valores: [
                                    +Number(dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionFinal?.dimensionAmbiental ?? 0).toFixed(1),
                                    +Number(dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionFinal?.dimensionSocioeconomica ?? 0).toFixed(1),
                                    +Number(dataIPPTA?.ippta?.nivelTransicionAgroecologica?.medicionFinal?.dimensionProductiva ?? 0).toFixed(1),
                                ],
                            }]}
                            colores={CHART_COLORS.ippta}
                        />
                    </GraficaCard>
                </Col>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="Medición General (# registros)" minHeight={280}>
                        <GraficaTorta3D
                            datos={[
                                { name: "Medición Inicial", valor: dataIPPTA?.ippta?.medicionGeneral?.medicionInicial ?? 0 },
                                { name: "Medición Final",   valor: dataIPPTA?.ippta?.medicionGeneral?.medicionFinal ?? 0 },
                            ]}
                            colores={CHART_COLORS.medGen}
                        />
                    </GraficaCard>
                </Col>
            </Row>

            {/* ── EDHA ─────────────────────────────────────────────────── */}
            <SeccionTitulo icono={HeartOutlined} titulo="EDHA — Evaluación de Dieta y Hábitos Alimentarios" color={COLORS.slate} />
            <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="Participantes por medición" minHeight={260}>
                        <GraficaBarras
                            etiquetas={["Inicial", "Intermedia", "Final"]}
                            datos={[{
                                label: "Participantes",
                                valores: [
                                    dataEDHA?.dataEDHA?.edha?.participantesxmediccion?.inicial ?? 0,
                                    dataEDHA?.dataEDHA?.edha?.participantesxmediccion?.intermedia ?? 0,
                                    dataEDHA?.dataEDHA?.edha?.participantesxmediccion?.final ?? 0,
                                ],
                            }]}
                            colores={CHART_COLORS.edha}
                        />
                    </GraficaCard>
                </Col>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="Encuestas por municipio" minHeight={260}>
                        <GraficaBarrasHorizontal
                            etiquetas={Object.keys(dataEDHA?.dataEDHA?.edha?.Encuestaxmunicipio ?? {})}
                            datos={[{
                                label: "Encuestas",
                                valores: Object.values(dataEDHA?.dataEDHA?.edha?.Encuestaxmunicipio ?? {}),
                            }]}
                            colores={[COLORS.teal]}
                        />
                    </GraficaCard>
                </Col>
                <Col xs={24} md={8}>
                    <GraficaCard titulo="Componente comercial — Tipos de acuerdo" minHeight={260}>
                        <GraficaBarras
                            etiquetas={componenteComercial?.dataCompComercial?.map((i) => i.tipoAcuerdo) ?? []}
                            datos={[{
                                label: "Acuerdos",
                                valores: componenteComercial?.dataCompComercial?.map((i) => i.total) ?? [],
                            }]}
                            colores={CHART_COLORS.comercial}
                        />
                    </GraficaCard>
                </Col>
            </Row>

            {/* ── CARACTERIZACIÓN DE PARTICIPANTES ─────────────────────── */}
            <SeccionTitulo icono={TeamOutlined} titulo="CARACTERIZACIÓN DE PARTICIPANTES" color={COLORS.primary} />

            <Collapse
                accordion
                style={{ borderRadius: 12, overflow: "hidden" }}
                items={dataProyecto
                    .filter((c) => c.sigla === "LP")
                    .map((item) => ({
                        key: item.oid,
                        label: (
                            <Space>
                                <TeamOutlined style={{ color: COLORS.primary }} />
                                <Text strong>Detalle de participantes — {item.nombre || "Componente LP"}</Text>
                                <Tag color="blue">{totalParticipante} participantes</Tag>
                            </Space>
                        ),
                        children: dataParticipantes ? (
                            <div style={{ padding: "8px 0" }}>

                                {/* Resumen ubicación */}
                                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                                    <Col xs={24} sm={8}>
                                        <Card style={{ borderRadius: 10, textAlign: "center", borderLeft: `4px solid ${COLORS.primary}` }}
                                              styles={{ body: { padding: 16 } }}>
                                            <Statistic
                                                title="Total Participantes"
                                                value={totalParticipante}
                                                prefix={<UsergroupAddOutlined style={{ color: COLORS.primary }} />}
                                                valueStyle={{ color: COLORS.primary, fontWeight: 700 }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Card style={{ borderRadius: 10, textAlign: "center", borderLeft: `4px solid ${COLORS.secondary}` }}
                                              styles={{ body: { padding: 16 } }}>
                                            <Statistic
                                                title="Departamento"
                                                value={department?.departamento ?? "N/D"}
                                                valueStyle={{ color: COLORS.secondary, fontSize: 16 }}
                                                prefix={<BarChartOutlined style={{ color: COLORS.secondary }} />}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                        <Card style={{ borderRadius: 10, textAlign: "center", borderLeft: `4px solid ${COLORS.accent}` }}
                                              styles={{ body: { padding: 16 } }}>
                                            <Statistic
                                                title="Municipio principal"
                                                value={muni?.municipio ?? "N/D"}
                                                valueStyle={{ color: COLORS.accent, fontSize: 16 }}
                                                prefix={<EnvironmentOutlined style={{ color: COLORS.accent }} />}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Sexo + Escolaridad + Población */}
                                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                                    <Col xs={24} md={8}>
                                        <GraficaCard titulo="Distribución por sexo" minHeight={240}>
                                            <GraficaTorta
                                                datos={participantesPorSexo.map((i) => ({
                                                    name: i.sexo,
                                                    valor: i.total_por_sexo ?? 0,
                                                }))}
                                                colores={CHART_COLORS.sexo}
                                            />
                                        </GraficaCard>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <GraficaCard titulo="Nivel de escolaridad" minHeight={240}>
                                            <GraficaBarras
                                                etiquetas={Object.keys(caracterizacionParticipantes?.nivelEscolaridad ?? {})}
                                                datos={[{ label: "Escolaridad", valores: Object.values(caracterizacionParticipantes?.nivelEscolaridad ?? {}) }]}
                                                colores={[COLORS.primary]}
                                            />
                                        </GraficaCard>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <GraficaCard titulo="Tipo de población" minHeight={240}>
                                            <GraficaBarras
                                                etiquetas={Object.keys(caracterizacionParticipantes?.tipoPoblacion ?? {})}
                                                datos={[{ label: "Población", valores: Object.values(caracterizacionParticipantes?.tipoPoblacion ?? {}) }]}
                                                colores={[COLORS.secondary]}
                                            />
                                        </GraficaCard>
                                    </Col>
                                </Row>

                                {/* Categorías */}
                                {conteoPorCategorias.map((cat, i) => {
                                    const metricas = [
                                        { label: "Campesino",    valor: cat.total_campesino,      color: COLORS.primary },
                                        { label: "Com. NARP",    valor: cat.total_comunidad_narp, color: COLORS.danger },
                                        { label: "Víctimas",     valor: cat.total_victimas,       color: "#cf1322" },
                                        { label: "Mujer Rural",  valor: cat.total_mujer_rural,    color: COLORS.accent },
                                        { label: "Joven Rural",  valor: cat.total_joven_rural,    color: COLORS.purple },
                                        { label: "Adulto Mayor", valor: cat.total_adulto_mayor,   color: COLORS.teal },
                                    ];
                                    return (
                                        <div key={i} style={{ marginBottom: 16 }}>
                                            <Divider orientation="left" style={{ fontSize: 13, color: "#666" }}>
                                                Participantes por Categoría
                                            </Divider>
                                            <Row gutter={[12, 12]}>
                                                {metricas.map((m, idx) => (
                                                    <Col key={idx} xs={12} sm={8} md={4}>
                                                        <Tooltip title={`${m.label}: ${m.valor ?? 0} personas`} placement="top">
                                                            <Badge.Ribbon text={m.label} color={m.color}>
                                                                <Card hoverable style={{ borderRadius: 10, textAlign: "center", height: "100%" }}
                                                                      styles={{ body: { padding: "24px 12px 12px" } }}>
                                                                    <UserOutlined style={{ fontSize: 22, color: m.color, display: "block", marginBottom: 4 }} />
                                                                    <Text strong style={{ fontSize: 22, color: m.color }}>{m.valor ?? 0}</Text>
                                                                </Card>
                                                            </Badge.Ribbon>
                                                        </Tooltip>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    );
                                })}

                                {/* ── Escolaridad detallada + Estado civil ── */}
                                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                                    <Col xs={24} md={14}>
                                        <Divider orientation="left" style={{ fontSize: 13, color: "#666" }}>
                                            Escolaridad detallada
                                        </Divider>
                                        <Row gutter={[12, 12]}>
                                            {participantesPorNivelEscolaridad.map((item, idx) => (
                                                <Col key={idx} xs={24} sm={12} md={12}>
                                                    <EscolaridadCard item={item} total={totalParticipante} index={idx} />
                                                </Col>
                                            ))}
                                        </Row>
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <Divider orientation="left" style={{ fontSize: 13, color: "#666" }}>
                                            Estado civil
                                        </Divider>
                                        <Row gutter={[12, 12]}>
                                            {participantesPorEstadoCivil.map((item, idx) => (
                                                <Col key={idx} xs={24} sm={12}>
                                                    <EstadoCivilCard item={item} total={totalParticipante} index={idx} />
                                                </Col>
                                            ))}
                                        </Row>
                                    </Col>
                                </Row>

                                {/* Organizaciones */}
                                <Divider orientation="left" style={{ fontSize: 13, color: "#666", marginTop: 20 }}>
                                    Pertenencia a Organizaciones
                                </Divider>
                                <Row gutter={[12, 12]}>
                                    {organizaciones.map((item, idx) => {
                                        const maxOrg = Math.max(...organizaciones.map((o) => o.total), 1);
                                        const pct = Math.round((item.total / maxOrg) * 100);
                                        const color = [COLORS.primary, COLORS.secondary, COLORS.accent][idx % 3];
                                        return (
                                            <Col key={idx} xs={24} sm={8}>
                                                <Tooltip title={`${item.total} participantes vinculados`} placement="top">
                                                    <Card
                                                        style={{ borderRadius: 10, borderTop: `3px solid ${color}` }}
                                                        styles={{ body: { padding: 14 } }}
                                                        hoverable
                                                    >
                                                        <Text strong style={{ fontSize: 13, display: "block", marginBottom: 8 }}>
                                                            {item.vinculado_alguna_organizacion || "N/A"}
                                                        </Text>
                                                        <Progress
                                                            percent={pct}
                                                            strokeColor={color}
                                                            format={() => (
                                                                <Text style={{ color, fontWeight: 700 }}>{item.total}</Text>
                                                            )}
                                                        />
                                                    </Card>
                                                </Tooltip>
                                            </Col>
                                        );
                                    })}
                                </Row>

                                {/* Cabeza de familia */}
                                <Divider orientation="left" style={{ fontSize: 13, color: "#666", marginTop: 20 }}>
                                    Cabeza de Familia
                                </Divider>
                                <Row gutter={[16, 16]}>
                                    {cabezaFamiliar.map((item, idx) => {
                                        const pct = totalCabeza > 0 ? +((item.cantidad / totalCabeza) * 100).toFixed(1) : 0;
                                        const esSi = item.cabeza_familia === "SI";
                                        const color = esSi ? COLORS.secondary : COLORS.danger;
                                        return (
                                            <Col key={idx} xs={24} sm={12}>
                                                <Card style={{ borderRadius: 10, textAlign: "center" }} styles={{ body: { padding: 16 } }}>
                                                    <Progress
                                                        type="circle"
                                                        percent={Math.round(pct)}
                                                        format={() => (
                                                            <span style={{ fontSize: 18, fontWeight: 700, color }}>{item.cantidad}</span>
                                                        )}
                                                        size={90}
                                                        strokeColor={color}
                                                    />
                                                    <div style={{ marginTop: 10 }}>
                                                        <Tag color={esSi ? "green" : "red"} style={{ fontSize: 13 }}>
                                                            {esSi ? "Jefe de hogar: SÍ" : "Jefe de hogar: NO"}
                                                        </Tag>
                                                        <Text style={{ display: "block", fontSize: 12, color: "#888", marginTop: 4 }}>
                                                            {pct}% del total
                                                        </Text>
                                                    </div>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>

                                {/* Línea económica */}
                                <Divider orientation="left" style={{ fontSize: 13, color: "#666", marginTop: 20 }}>
                                    Línea Productiva
                                </Divider>
                                <Table
                                    dataSource={lineaEconomica}
                                    columns={columnsLineap}
                                    pagination={{ pageSize: 8, size: "small" }}
                                    rowKey="linea_economica"
                                    size="small"
                                    style={{ borderRadius: 10, overflow: "hidden" }}
                                    rowClassName={(_, idx) => (idx % 2 === 0 ? "" : "ant-table-row-alt")}
                                />

                                {/* Zonas */}
                                <Divider orientation="left" style={{ fontSize: 13, color: "#666", marginTop: 20 }}>
                                    Zonas
                                </Divider>
                                <Row gutter={[12, 12]}>
                                    {zona.map((z, idx) => (
                                        <Col key={idx} xs={24} sm={12} md={8}>
                                            <Tooltip title={`${z.total_por_zona} participantes en esta zona`} placement="top">
                                                <Card
                                                    style={{ borderRadius: 10, borderLeft: `4px solid ${COLORS.teal}` }}
                                                    styles={{ body: { padding: 14 } }}
                                                    hoverable
                                                >
                                                    <Space>
                                                        <HomeOutlined style={{ color: COLORS.teal, fontSize: 18 }} />
                                                        <div>
                                                            <Text strong style={{ fontSize: 13 }}>{z.nombre_zona}</Text>
                                                            <Text style={{ display: "block", fontSize: 12, color: "#888" }}>
                                                                {z.total_por_zona} participantes
                                                            </Text>
                                                        </div>
                                                    </Space>
                                                </Card>
                                            </Tooltip>
                                        </Col>
                                    ))}
                                </Row>

                            </div>
                        ) : (
                            <Alert type="info" message="No hay datos de participantes disponibles para este proyecto." showIcon />
                        ),
                    }))}
            />

            <div style={{ height: 40 }} />
        </div>
    );
};

export default DashboardPlanOperativo;